import { useCallback, useEffect } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';

import {
  IKnmErknmFormViolationEventMaterial,
  IKnmErknmFormViolationEventMaterialVideoFixation,
} from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { ErknmActViolationEventMaterialItem } from 'KnmErknmPackage/ErknmViolationsModule/components/ErknmActViolationEventMaterialItem';

interface IErknmActViolationEventMaterialsProps {
  name: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  id: string;
  canEdit: boolean;
  selectedEventGuid?: string | null;
}

const createEmptyVideoFixation = (): IKnmErknmFormViolationEventMaterialVideoFixation => ({
  guid: uuidv4(),
  videoFixationOption: null,
});

const createEmptyMaterial = (isDecision?: boolean): Partial<IKnmErknmFormViolationEventMaterial> => ({
  isNew: true,
  guid: uuidv4(),
  videoFixations: isDecision ? [createEmptyVideoFixation()] : null,
  typeDocConfirmationViolation: null,
  attachments: null,
  links: null,
});

export const ErknmActViolationEventMaterials = ({
  name,
  formRef,
  isKnm,
  isPm,
  isDecision,
  id,
  canEdit,
  selectedEventGuid,
}: IErknmActViolationEventMaterialsProps) => {
  const materialsFieldName = `${name}.materials`;
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  // ИР: один material-обёртка, список подтверждающих материалов — videoFixations[]
  useEffect(() => {
    if (!isDecision || !formRef?.current) {
      return;
    }

    // @ts-ignore
    const materials = formRef.current.getFieldState(materialsFieldName as keyof IDecisionFormRef)?.value as
      | IKnmErknmFormViolationEventMaterial[]
      | undefined;

    if (materials?.length) {
      return;
    }

    // @ts-ignore
    formRef.current.change(materialsFieldName as keyof IDecisionFormRef, [
      {
        guid: uuidv4(),
        isNew: true,
        videoFixations: [createEmptyVideoFixation()],
        typeDocConfirmationViolation: null,
        attachments: null,
        links: null,
      },
    ]);
  }, [formRef, isDecision, materialsFieldName]);

  const handleAddMaterial = useCallback(() => {
    if (isDecision) {
      // @ts-ignore
      const materials = formRef?.current?.getFieldState(materialsFieldName as keyof IDecisionFormRef)?.value as
        | IKnmErknmFormViolationEventMaterial[]
        | undefined;
      const materialGuid = materials?.[0]?.guid || uuidv4();
      const videoFixationsPath = `${materialsFieldName}[0].videoFixations`;

      if (!materials?.length) {
        // @ts-ignore
        formRef?.current?.change(materialsFieldName as keyof IDecisionFormRef, [
          {
            guid: materialGuid,
            isNew: true,
            videoFixations: [createEmptyVideoFixation()],
            typeDocConfirmationViolation: null,
            attachments: null,
            links: null,
          },
        ]);

        return;
      }

      push && push(videoFixationsPath, createEmptyVideoFixation());

      return;
    }

    push && push(materialsFieldName, createEmptyMaterial(false));
  }, [formRef, isDecision, materialsFieldName, push]);

  const getRemoveHandler = useCallback(
    (index: number) => () => {
      remove && remove(materialsFieldName, index);
    },
    [materialsFieldName, remove],
  );

  return (
    <>
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: isDecision ? 'Подтверждающий материал' : 'Сведения о фиксации нарушений',
          field: isDecision ? `${materialsFieldName}[0].videoFixations` : materialsFieldName,
          isPm,
          isKnm,
          isDecision,
        })}
        margin="top"
        id={`${id}.materials`}
        button={
          canEdit ? (
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              onClick={handleAddMaterial}
            />
          ) : undefined
        }
      />
      <FieldArray name={materialsFieldName}>
        {({ fields, meta }) => (
          <>
            {fields.map((materialFieldName, index) => {
              const materialValue: IKnmErknmFormViolationEventMaterial = fields.value[index];

              return (
                <ErknmActViolationEventMaterialItem
                  key={materialValue.guid}
                  material={materialValue}
                  materialIdx={index}
                  fieldName={materialFieldName}
                  formRef={formRef}
                  canEdit={canEdit}
                  onRemove={getRemoveHandler(index)}
                  isKnm={isKnm}
                  isPm={isPm}
                  isDecision={isDecision}
                  id={`${id}.materials[${index}]`}
                  selectedEventGuid={selectedEventGuid}
                />
              );
            })}
            {typeof meta.error === 'string' && meta.touched && (
              <p className="shared-input-error shared-row__small-margin">{meta.error}</p>
            )}
          </>
        )}
      </FieldArray>
    </>
  );
};
